// MUNYA OS · Banner "Modo Ver como X" persistente
// § 5.1 · A1 · visible en top-bar mientras hay impersonación activa
//
// Polling cada 60s para detectar expiración + botón "Volver a mi sesión".

import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Eye, X } from '@phosphor-icons/react';

interface ImpersonationState {
  impersonating: boolean;
  target_user_id?: string;
  target_role?: string;
  target_upn?: string;
  target_name?: string;
  actor_user_id?: string;
  actor_role?: string;
  expires_at?: number;
}

export function ImpersonationBanner(): ReactElement | null {
  const [state, setState] = useState<ImpersonationState>({ impersonating: false });
  const [stopping, setStopping] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/auth/impersonation', { credentials: 'include' });
      if (!res.ok) return;
      const data = (await res.json()) as ImpersonationState;
      setState(data);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    void load();
    const id = window.setInterval(load, 60_000);
    return () => {
      window.clearInterval(id);
    };
  }, [load]);

  async function stop(): Promise<void> {
    setStopping(true);
    try {
      await fetch('/api/v1/auth/stop-impersonating', {
        method: 'POST',
        credentials: 'include',
      });
      // Recarga para que la SPA refresque el rol efectivo
      window.location.reload();
    } catch {
      setStopping(false);
    }
  }

  if (!state.impersonating) return null;

  const minutesLeft = state.expires_at
    ? Math.max(0, Math.floor((state.expires_at * 1000 - Date.now()) / 60000))
    : 0;

  return (
    <div
      role="alert"
      className="sticky top-0 z-50 flex items-center justify-between gap-3 bg-[var(--color-munya-warning)]/95 border-b border-[var(--color-munya-warning)] px-4 py-1.5 text-sm text-[var(--color-munya-coffee)]"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Eye size={16} weight="duotone" />
        <span className="font-medium">Estás viendo como</span>
        <span className="font-semibold truncate">{state.target_name ?? state.target_upn}</span>
        <span className="opacity-70 text-xs">({state.target_role})</span>
        {minutesLeft > 0 && (
          <span className="opacity-60 text-xs">· {minutesLeft} min restantes</span>
        )}
      </div>
      <button
        type="button"
        onClick={() => void stop()}
        disabled={stopping}
        className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded bg-[var(--color-munya-coffee)] text-[var(--color-munya-cream)] disabled:opacity-50"
      >
        <X size={12} /> Volver a mi sesión
      </button>
    </div>
  );
}
